import Application from 'src/Application';
import Spinner from 'src/game/components/shared/Spinner';
import {
  isPopupId,
  popupRegistry,
  PopupID,
} from 'src/game/components/popups/popupRegistry';
import { createPersistentEmitter } from 'src/lib/Emitter';
import { closePopup } from 'src/lib/popups/popupOpenClose';

import PopupSettings from 'src/game/components/popups/PopupSettings';
import PopupCheats from 'src/game/components/popups/PopupCheats';
import PopupTestPopups from 'src/game/components/popups/PopupTestPopups';
import createCheatsButton from 'src/game/components/shared/ButtonCheats';

export default class PopupManager {
  private app: Application;

  constructor(opts: { app: Application }) {
    this.app = opts.app;

    this.createPopups();

    const openPopups: {
      [K in PopupID]?: { onClose: (...args: any) => void };
    } = {};

    createPersistentEmitter((state) => state.ui.togglePopup).addListener(
      ({ id, enabled, opts }) => {
        if (!id) {
          return; // No command...
        }
        if (!isPopupId(id)) {
          throw new Error(`Trying to toggle unknown popup: '${id}'.`);
        }

        if (enabled) {
          if (id in openPopups) {
            // Could happen if somebody dispatches togglePopup directly.
            throw new Error(`Uncaught double open popup: '${id}'.`);
          }

          openPopups[id] = popupRegistry[id]({
            superview: this.app.getRootView(),
            openArgs: opts,
            close: (...args: any) => (closePopup as any)(id, ...args),
          });
        } else {
          if (!(id in openPopups)) {
            // Could happen if somebody dispatches togglePopup directly.
            throw new Error(`Uncaught double close popup: '${id}'.`);
          }

          const { onClose } = openPopups[id];

          // Clean up before `onClose` (potentially) crashes
          delete openPopups[id];

          onClose(...opts.args);
        }
      },
      { noInit: true },
    );
  }

  private createPopups() {
    // ===== spinner

    new Spinner({ superview: this.app.getRootView() });

    // ===== cheat popups

    if (process.env.IS_DEVELOPMENT) {
      // The cheats popup requires a special `app` argument.
      popupRegistry.popupCheats = ({ openArgs, ...creationOpts }) => {
        const popup = new PopupCheats({ ...creationOpts, app: this.app });
        popup.init(openArgs);
        return { onClose: () => popup.fadeOut() };
      };

      popupRegistry.popupTestPopups = ({ openArgs, ...creationOpts }) => {
        const popup = new PopupTestPopups({ ...creationOpts });
        popup.init(openArgs);
        return { onClose: () => popup.fadeOut() };
      };

      createCheatsButton({ superview: this.app.getRootView() });
    }
  }
}
